// ============================================================
// TargetDefenderTab - Persistent defence reference panel.
// Shows dodge/parry skill values, defence talents, custom mods,
// and a defence probability carousel.
// ============================================================
import { sideOf } from '../engine/roster/team';
import { memo, useContext } from 'react';
import RosterContext from '../contexts/RosterContext';
import { reactionExposure } from '../engine/exposures';
import { attackingMagnitude } from '../engine/bodyShape';
import { projectDefender } from '../engine/roster/projection';
import type { Combatant } from '../engine/roster/types';
import { STANDING_REACTIONS } from '../engine/standingReaction';
import type { StandingReaction } from '../engine/standingReaction';
import type { Target, Slot, DefenderCustomMod } from '../types';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import type { Character, AttackerCombatStats } from '../engine/character/types';
import { applyDefenderMods } from '../engine/defenceCalc';
import useDefenceProbability from '../hooks/useDefenceProbability';
import DefenceProbabilityCarousel from './DefenceProbabilityCarousel';
import { DefenderModsCard } from './DefenderModsCard';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/TargetDefenderTab.module.css';
import type { ConditionModContext, ConditionModOptions } from '../engine/conditionModifiers';

interface Props {
  /** Whose sheet this is (v9.5.0): the actor wears the gold grammar, the target the auspex red. */
  tone?: 'actor' | 'target';
  target: Target;
  sourceCharacter: Character | null;
  defenderCustomMods: DefenderCustomMod[];
  onAddMod: (mod: DefenderCustomMod) => void;
  onEditMod: (id: string, updates: Partial<Pick<DefenderCustomMod, 'name' | 'value'>>) => void;
  onRemoveMod: (id: string) => void;
  onToggleFrenzied?: () => void;
  /** The standing reaction (v8.325.0): absent = ask. */
  onSetStandingReaction?: (value: StandingReaction | null) => void;
  locked: boolean;
  // Attacker context for probability engine
  slot?: Slot | null;
  fireMode?: string;
  isMelee?: boolean;
  attackMod?: number;
  rangeBand?: string;
  hatred?: boolean;
  /** The attacker's re-roll damage pill, for the damage forecast (v8.392.4). */
  rerollDamage?: boolean;
  attackerCharStats?: AttackerCombatStats | null;
  /** The actor as the roll reads them: their conditions and the auras they stand under (2026-10-20). */
  attackerConditions?: ConditionModContext | null;
  /** Unnatural DoS house rule - forecasts mirror the live math. */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Condition-modifier house rule - forecasts mirror the live defence chain. */
  applyConditionModifiers?: boolean;
  /** Which condition modifiers the table has switched off. */
  condModOptions?: ConditionModOptions;
}

function TargetDefenderTab(props: Props) {
  const { target, defenderCustomMods, locked } = props;
  const baseDodge = target.dodge?.agility || 0;
  const baseParry = target.parry?.weaponSkill || 0;
  // Defensive Stance grants no +10 since v8.216.0 - RAW puts -20 on the
  // ATTACKER via the Ds condition's posture rule instead.
  const { effectiveDodge, effectiveParry } = applyDefenderMods(baseDodge, baseParry, defenderCustomMods);
  const additionalDodges = (target.dodge as any)?.additionalDodges || 0;
  const additionalParries = (target.parry as any)?.additionalParries || 0;
  const { result, loading } = useDefenderForecast(props);
  return (
    <div className={styles.tab} data-tone={props.tone ?? 'target'}>
      <FrenzyCard target={target} locked={locked} onToggle={props.onToggleFrenzied} />
      {props.onSetStandingReaction && (
        <StandingReactionCard target={target} locked={locked} onSet={props.onSetStandingReaction} />
      )}
      <SkillCard
        skill="DODGE"
        trained={!!target.dodge?.canDodge}
        base={baseDodge}
        effective={effectiveDodge}
        extra={extraRow('Step Aside', additionalDodges, 'dodge', ['', 's'])}
      />
      <SkillCard
        skill="PARRY"
        trained={!!target.parry?.canParry}
        powerField={!!target.parry?.hasPowerField}
        base={baseParry}
        effective={effectiveParry}
        extra={extraRow('Wall of Steel', additionalParries, 'parr', ['y', 'ies'])}
      />
      <DefenceTalentsCard target={target} />
      <DefenderModsCard
        mods={defenderCustomMods}
        onAdd={props.onAddMod}
        onEdit={props.onEditMod}
        onRemove={props.onRemoveMod}
        locked={locked}
      />
      {/* Defence Probability Carousel - fills remaining space */}
      <DefenceProbabilityCarousel
        result={result}
        loading={loading}
        hasAttackerContext={!!(props.slot && !props.slot.isEmpty)}
      />
    </div>
  );
}

/**
 * The defence forecast against the current attacker. The actor's team,
 * so a strict @hostile / @ally exposure reads the same in the forecast as
 * in the roll (v8.368.0), and the attacker's reaction exposures against
 * this defender (v8.368.3), as the freeze would set them. The roster is
 * read tolerantly: the tab also renders in tests without a provider.
 */
function useDefenderForecast(props: Props) {
  const { target, isMelee, attackerCharStats } = props;
  // Read tolerantly: the tab also renders in tests without a provider.
  const roster = useContext(RosterContext) as { actor?: Combatant | null } | null;
  const attackerTeamTag = roster?.actor?.sheet?.team?.tag?.trim() || null;
  const attackerSideTag = sideOf(roster?.actor?.sheet?.team);
  const reaction = reactionExposure(
    {
      conditions: roster?.actor?.state?.conditions ?? [],
      exposures: roster?.actor?.sheet?.exposures ?? [],
      teamTag: attackerTeamTag,
      sideTag: attackerSideTag,
    },
    { teamTag: target.teamTag ?? null, sideTag: target.sideTag ?? null, isMelee: !!isMelee },
  );
  const attackerReactionMods =
    reaction.dodgeMod || reaction.parryMod ? { dodge: reaction.dodgeMod, parry: reaction.parryMod } : null;
  return useDefenceProbability({
    target,
    defenderCustomMods: props.defenderCustomMods,
    slot: props.slot ?? null,
    fireMode: props.fireMode,
    isMelee,
    attackMod: props.attackMod,
    rangeBand: props.rangeBand,
    hatred: props.hatred,
    rerollDamage: props.rerollDamage,
    damageBonusMelee: attackerCharStats?.damageBonusMelee ?? 0,
    damageBonusRanged: attackerCharStats?.damageBonusRanged ?? 0,
    attackerTeamTag,
    attackerSideTag,
    // A horde attacking scores +1 hit per 10 magnitude: the forecast reads the actor's live magnitude, as the roll.
    attackerMagnitude: roster?.actor ? attackingMagnitude(projectDefender(roster.actor)) : 0,
    attackerReactionMods,
    attackerConditions: props.attackerConditions ?? null,
    unnaturalDosMode: props.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE,
    skillCharValue: attackerCharStats?.skillCharValue ?? 0,
    skillUnnaturalMultiplier: attackerCharStats?.skillUnnaturalMultiplier ?? 1,
    applyConditionModifiers: props.applyConditionModifiers ?? false,
    condModOptions: props.condModOptions,
  });
}

function FrenzyCard({ target, locked, onToggle }: { target: Target; locked: boolean; onToggle?: () => void }) {
  const active = target.frenzied ? 'true' : 'false';
  return (
    <div className={clsx(styles.card, styles.frenzyCard)} data-active={active}>
      <label className={styles.frenzyLabel} data-locked={locked ? 'true' : undefined}>
        <input
          type="checkbox"
          checked={target.frenzied || false}
          onChange={() => !locked && onToggle?.()}
          disabled={locked}
        />
        <span className={styles.frenzyName} data-active={active}>
          Frenzied
        </span>
        <span className={styles.frenzyNote}>Blocks Dodge & Parry</span>
      </label>
      {target.frenzied && (target as any).hasBattleRage && (
        <div className={styles.frenzyRage}>Battle Rage: Parry re-enabled.</div>
      )}
    </div>
  );
}

interface StandingProps {
  target: Target;
  locked: boolean;
  onSet: (value: StandingReaction | null) => void;
}

/**
 * THE STANDING REACTION (v8.325.0): what they do when shot at unless told
 * otherwise. Ask me is the default for everyone; a standing choice answers
 * the prompt after a countdown, and a click still overrides it.
 */
function StandingReactionCard({ target, locked, onSet }: StandingProps) {
  return (
    <div className={styles.card} data-testid="defender-standing-card">
      <label className={styles.skillHeader} style={{ gap: 8 }}>
        <span className={styles.skillName}>WHEN SHOT AT</span>
        <select
          className={common.input}
          data-testid="standing-reaction"
          aria-label="Standing reaction"
          value={target.standingReaction ?? ''}
          disabled={locked}
          onChange={(e) => onSet((e.target.value || null) as StandingReaction | null)}
        >
          {STANDING_REACTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** Each skill card's own colours and test id. */
const SKILL_STYLE = {
  DODGE: { testId: 'defender-dodge-card', name: styles.skillNameDodge, badge: styles.badgeDodge },
  PARRY: { testId: 'defender-parry-card', name: styles.skillNameParry, badge: styles.badgeParry },
};

interface SkillCardProps {
  skill: keyof typeof SKILL_STYLE;
  trained: boolean;
  powerField?: boolean;
  base: number;
  effective: number;
  /** The extra-reaction talent row: [label, value], or null. */
  extra: [string, string] | null;
}

/** A reaction skill: trained or not, its base and effective value, and any extra reactions. */
function SkillCard({ skill, trained, powerField = false, base, effective, extra }: SkillCardProps) {
  const look = SKILL_STYLE[skill];
  return (
    <div className={styles.card} data-testid={look.testId}>
      <div className={styles.skillHeader}>
        <span className={clsx(styles.skillName, look.name)}>{skill}</span>
        <span className={clsx(styles.badge, trained ? look.badge : styles.badgeOff)}>
          {trained ? 'Trained' : 'Untrained'}
        </span>
        {powerField && <span className={clsx(styles.badge, styles.badgeField)}>Power Field</span>}
      </div>
      <div className={styles.grid}>
        <div className={styles.gridItem}>
          <span>Base</span>
          <span className={styles.gridValue}>{base}</span>
        </div>
        <div className={styles.gridItem}>
          <span>Effective</span>
          <span className={styles.gridValue} data-delta={deltaOf(effective, base)}>
            {effective}
          </span>
        </div>
        {extra && (
          <div className={clsx(styles.gridItem, styles.gridSpan)}>
            <span>{extra[0]}</span>
            <span className={styles.gridValue}>{extra[1]}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The extra-reaction talent row ("+2 parries", "+2 dodges"), or null with
 * none: the stem, then the singular or plural ending.
 */
function extraRow(label: string, n: number, stem: string, [one, many]: [string, string]): [string, string] | null {
  return n > 0 ? [label, `+${n} ${stem}${n > 1 ? many : one}`] : null;
}

/** Which way the modifiers moved a skill: 'up', 'down', or undefined for unchanged. */
function deltaOf(effective: number, base: number): 'up' | 'down' | undefined {
  if (effective === base) return undefined;
  return effective > base ? 'up' : 'down';
}

const DEFENCE_TALENTS: { label: string; key: 'counterAttackAvailable' | 'trueGrit' | 'hardy' | 'ironJaw' }[] = [
  { label: 'Counter-Attack', key: 'counterAttackAvailable' },
  { label: 'True Grit', key: 'trueGrit' },
  { label: 'Hardy', key: 'hardy' },
  { label: 'Iron Jaw', key: 'ironJaw' },
];

function DefenceTalentsCard({ target }: { target: Target }) {
  return (
    <div className={styles.card} data-testid="defender-talents">
      <div className={styles.cardTitle}>DEFENCE TALENTS</div>
      <div className={styles.grid}>
        {DEFENCE_TALENTS.map((t) => (
          <div key={t.key} className={styles.gridItem}>
            <span>{t.label}</span>
            <span className={styles.gridValue} data-on={target[t.key] ? 'true' : 'false'}>
              {target[t.key] ? 'Yes' : 'No'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default memo(TargetDefenderTab);
